import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Lock, ShieldCheck, AlertTriangle, Clock } from "lucide-react";

/**
 * EscrowStatusPill — renders the booking's escrow state with role-aware
 * actions: clients see Dispute, providers see Release. Used inside the
 * Wallet/Bookings list rows.
 *
 * Props:
 *   - booking (the booking row)
 *   - viewerRole ("CLIENT" or "PROVIDER" — controls which actions show)
 *   - onUpdated (callback after successful action; parent should refetch)
 */
export default function EscrowStatusPill({ booking, viewerRole, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [reason, setReason] = useState("");

  const status = booking.escrowStatus;
  if (!status) return null;

  const meta = {
    held: { Icon: Lock, label: "Funds in escrow", color: "text-foreground/75" },
    released: { Icon: ShieldCheck, label: "Released", color: "text-secondary" },
    disputed: { Icon: AlertTriangle, label: "Disputed — under review", color: "text-orange-300" },
    refunded: { Icon: AlertTriangle, label: "Refunded to client", color: "text-foreground/55" },
  }[status] || { Icon: Clock, label: status, color: "text-foreground/55" };
  const { Icon, label, color } = meta;

  const release = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/release`);
      toast.success("Funds released");
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Release failed");
    } finally { setBusy(false); }
  };

  const submitDispute = async () => {
    if (reason.trim().length < 10) {
      toast.error("Please add a reason (min 10 characters)");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/dispute`, { reason: reason.trim() });
      toast.success("Dispute filed — admin will review");
      setDisputeOpen(false);
      setReason("");
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Dispute failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid={`escrow-pill-${booking.id}`}>
      <div className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] ${color}`}>
        <Icon size={12} strokeWidth={1.5} />
        {label}
        {status === "held" && booking.escrowAutoReleaseAt && (
          <span className="text-foreground/45 normal-case tracking-normal">
            · auto-releases {new Date(booking.escrowAutoReleaseAt).toLocaleDateString()}
          </span>
        )}
      </div>

      {status === "held" && viewerRole === "PROVIDER" && (
        <button
          onClick={release}
          disabled={busy}
          className="text-[11px] uppercase tracking-[0.16em] text-secondary hover:underline disabled:opacity-50"
          data-testid={`escrow-release-${booking.id}`}
        >
          Release funds now
        </button>
      )}

      {status === "held" && viewerRole === "CLIENT" && !disputeOpen && (
        <button
          onClick={() => setDisputeOpen(true)}
          className="text-[11px] uppercase tracking-[0.16em] text-orange-300 hover:underline"
          data-testid={`escrow-dispute-open-${booking.id}`}
        >
          Dispute this booking
        </button>
      )}

      {disputeOpen && (
        <div className="space-y-2 border border-orange-400/30 bg-orange-400/[0.04] rounded-sm p-3">
          <textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Tell us what went wrong (delivered late, no-show, content not as promised...)"
            className="w-full bg-input border border-secondary/20 px-3 py-2 rounded-sm focus:outline-none focus:border-secondary text-sm"
            data-testid={`escrow-dispute-reason-${booking.id}`}
          />
          <div className="flex gap-2">
            <button
              onClick={submitDispute}
              disabled={busy}
              className="text-[11px] uppercase tracking-[0.16em] bg-orange-400 text-background px-3 py-1.5 rounded-sm disabled:opacity-50"
              data-testid={`escrow-dispute-submit-${booking.id}`}
            >
              File Dispute
            </button>
            <button
              onClick={() => { setDisputeOpen(false); setReason(""); }}
              className="text-[11px] uppercase tracking-[0.16em] text-foreground/55"
              data-testid={`escrow-dispute-cancel-${booking.id}`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
